import { type ButtonHTMLAttributes } from 'react';
import { cn } from '../cn.js';

export type ButtonVariant = 'primary' | 'secondary';
export type ButtonShape = 'pill' | 'field';
export type ButtonSize = 'default' | 'action';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** primary: acción principal (una sola por paso del flujo); secondary: outline. */
  variant?: ButtonVariant;
  /** pill: tienda y chat; field: botones rectangulares del panel (--radius-field). */
  shape?: ButtonShape;
  /** Ocupa todo el ancho del contenedor (barras de acción en móvil). */
  fullWidth?: boolean;
  /**
   * default: área táctil ≥ 44 px. action: acción principal de un flujo (Crear, Pagar,
   * Registrar…), ≥ 50 px de alto (design-system §5.1).
   */
  size?: ButtonSize;
}

// Base común: foco visible obligatorio. La altura mínima la pone el tamaño (cn() no fusiona
// clases: una altura en className no puede sustituir a la del tamaño).
const BASE =
  'inline-flex items-center justify-center gap-2 px-5 font-body text-base font-medium ' +
  'border transition-colors duration-(--motion-fast) ease-standard outline-none ' +
  'focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50';

export const BUTTON_VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'border-transparent bg-accent text-accent-on hover:bg-accent-hover active:bg-accent-active',
  secondary: 'border-border-strong bg-surface text-fg hover:bg-bg active:bg-border-soft',
};

export const BUTTON_SIZE_CLASSES: Record<ButtonSize, string> = {
  default: 'min-h-touch', // --touch-target-min: 44 px
  action: 'min-h-action', // 50 px
};

const SHAPE_CLASSES: Record<ButtonShape, string> = {
  pill: 'rounded-pill',
  field: 'rounded-field',
};

/** Botón del design system. Por defecto type="button" para no enviar formularios sin querer. */
export function Button({
  variant = 'primary',
  shape = 'pill',
  fullWidth = false,
  size = 'default',
  type = 'button',
  className,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      data-variant={variant}
      className={cn(
        BASE,
        BUTTON_SIZE_CLASSES[size],
        BUTTON_VARIANT_CLASSES[variant],
        SHAPE_CLASSES[shape],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    />
  );
}
